import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { RequestTypes } from './requestTypes'
import { s3MediaKeys } from '../media/mediaKeys'
import { ISiteBoxDocument } from '@core/entities/SiteBoxDocument'

export const siteBoxDocumentsKeysQueryKeys = createQueryKeys(
  'siteBoxDocumentsKeys',
  {
    byId: ({ documentId }: RequestTypes['getDocument']) => {
      return {
        queryKey: [documentId],
        queryFn: async () => {
          const { data } = await APIAxiosInstance.get(
            `sitebox-documents/${documentId}`,
          )

          return data
        },
      }
    },
    byConfigId: ({
      organisationId,
      projectId,
      id,
    }: RequestTypes['getSiteBoxConfiguration']) => {
      return {
        queryKey: [organisationId, projectId, id],
        queryFn: async () => {
          const { data } = await APIAxiosInstance.get(
            `/organisations/${organisationId}/projects/${projectId}/sitebox-documents/config/${id}`,
          )

          return data
        },
      }
    },
    byVersionId: ({
      documentId,
      versionId,
    }: RequestTypes['getSiteBoxDocumentVersion']) => {
      return {
        queryKey: [documentId, versionId],
        queryFn: async () => {
          const { data } = await APIAxiosInstance.get(
            `/sitebox-documents/${documentId}/versions/${versionId}`,
          )
          return data
        },
      }
    },
  },
)

const siteBoxDocumentsKeysMutationsKeys = createMutationKeys(
  'siteBoxDocumentsKeys',
  {
    createDocument: {
      mutationKey: null,
      mutationFn: async ({
        organisationId,
        projectId,
        file,
        folderName,
        ...rest
      }: RequestTypes['createDocument']) => {
        let siteBoxDocumentKey = null

        if (file) {
          siteBoxDocumentKey = await (
            s3MediaKeys.uploadMedia as any
          ).mutationFn({ file, folderName })
        }

        const { data } = await APIAxiosInstance.post(
          `sitebox-documents/organisations/${organisationId}/projects/${projectId}`,
          {
            ...rest,
            version: {
              ...rest.version,
              documentKey: siteBoxDocumentKey,
            },
          },
        )

        return data as ISiteBoxDocument
      },
    },
    archiveDocument: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
        ...rest
      }: RequestTypes['archiveDocument']) => {
        const { data } = await APIAxiosInstance.post(
          `sitebox-documents/${documentId}/archive`,
          rest,
        )

        return data
      },
    },
    restoreDocument: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
        ...rest
      }: RequestTypes['restoreDocument']) => {
        const { data } = await APIAxiosInstance.post(
          `sitebox-documents/${documentId}/restore`,
          rest,
        )

        return data
      },
    },
    updateDocument: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
        ...rest
      }: RequestTypes['updateDocument']) => {
        const { data } = await APIAxiosInstance.put(
          `sitebox-documents/${documentId}`,
          rest,
        )

        return data
      },
    },
    updateSiteBoxConfiguration: {
      mutationKey: null,
      mutationFn: async ({
        organisationId,
        projectId,
        ...rest
      }: RequestTypes['updateSiteBoxConfiguration']) => {
        const { data } = await APIAxiosInstance.put(
          `/organisations/${organisationId}/projects/${projectId}/sitebox-documents/config`,
          rest,
        )
        return data
      },
    },
    processSiteBoxConfiguration: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
        ...rest
      }: RequestTypes['processSiteBoxConfiguration']) => {
        const { data } = await APIAxiosInstance.post(
          `sitebox-documents/${documentId}/process`,
          rest,
        )
        return data
      },
    },
    updateQueryAppraisal: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
        queryId,
        appraisal,
      }: RequestTypes['updateQueryAppraisal']) => {
        const { data } = await APIAxiosInstance.put(
          `appraisals/${documentId}/query/${queryId}`,
          appraisal,
        )
        return data
      },
    },
    updateDocumentAppraisal: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
        ...rest
      }: RequestTypes['updateDocumentAppraisal']) => {
        const { data } = await APIAxiosInstance.put(
          `appraisals/${documentId}/status`,
          rest,
        )
        return data
      },
    },
    createSiteboxAppraisalSheet: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
      }: RequestTypes['createSiteboxAppraisalSheet']) => {
        const { data } = await APIAxiosInstance.post(
          `appraisals/${documentId}/download`,
        )
        return data
      },
    },
    bypassReview: {
      mutationKey: null,
      mutationFn: async ({
        documentId,
        ...rest
      }: RequestTypes['bypassReview']) => {
        const { data } = await APIAxiosInstance.post(
          `appraisals/${documentId}/bypass`,
          rest,
        )
        return data
      },
    },
  },
)

export const siteBoxDocumentsKeys = mergeQueryKeys(
  siteBoxDocumentsKeysQueryKeys,
  siteBoxDocumentsKeysMutationsKeys,
).siteBoxDocumentsKeys
